import { useState } from "react";
import { Trash2, Loader2, CheckSquare, Square, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../ui/alert-dialog";

export const BulkActionBar = ({ selected, filteredIds, onSelectAll, onClear, onDelete }) => {
  const [busy, setBusy] = useState(false);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

  const confirm = async () => {
    setBusy(true);
    try {
      const n = await onDelete([...selected]);
      toast.success(`Deleted ${n} quote request${n === 1 ? "" : "s"}`);
      onClear();
    } catch {
      toast.error("Could not delete the selected leads");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
      <button
        type="button"
        data-testid="bulk-select-all"
        onClick={() => (allSelected ? onClear() : onSelectAll(filteredIds))}
        disabled={filteredIds.length === 0}
        className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-cyan-700 disabled:opacity-50"
      >
        {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
        {allSelected ? "Deselect all" : `Select all (${filteredIds.length})`}
      </button>
      {selected.size > 0 && (
        <>
          <span data-testid="bulk-selected-count" className="text-sm text-slate-600">{selected.size} selected</span>
          <button type="button" data-testid="bulk-clear" onClick={onClear} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                data-testid="bulk-delete-button"
                disabled={busy}
                className="ml-auto inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete {selected.size} selected
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="bulk-delete-dialog" className="bg-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-slate-900">Delete {selected.size} quote request{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600">
                  This permanently removes the selected leads with their notes and activity. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="bulk-delete-confirm" onClick={confirm} className="bg-red-600 hover:bg-red-700 text-white">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
};
